import { useCallback, useEffect } from "react";
import type { ScorecardData } from "@/types/scorecard";

interface ScorecardDraft {
	data: ScorecardData;
	/** Epoch ms of the last local edit, compared against scorecard_updated_at. */
	timestamp: number;
}

/**
 * localStorage draft persistence for the candidate scorecard, so unsaved
 * answers survive accidental navigation/refresh. Drafts are keyed by field id
 * (the `scorecard_draft_` prefix is new — older name-keyed backups under
 * `scorecard_backup_` are intentionally never restored).
 */
export function useScorecardDraft(
	candidateId: string | undefined,
	organizationId: string | undefined,
) {
	const storageKey = `scorecard_draft_${organizationId || "none"}_${candidateId || "none"}`;

	const readDraft = useCallback((): ScorecardDraft | null => {
		try {
			const raw = localStorage.getItem(storageKey);
			if (!raw) return null;
			const draft = JSON.parse(raw) as ScorecardDraft;
			if (!draft || typeof draft.timestamp !== "number" || typeof draft.data !== "object") {
				return null;
			}
			return draft;
		} catch {
			return null;
		}
	}, [storageKey]);

	const saveDraft = useCallback(
		(data: ScorecardData) => {
			try {
				localStorage.setItem(storageKey, JSON.stringify({ data, timestamp: Date.now() }));
			} catch {
				// Quota/private-mode failures just mean no draft safety net.
			}
		},
		[storageKey],
	);

	const clearDraft = useCallback(() => {
		try {
			localStorage.removeItem(storageKey);
		} catch {
			// Ignore storage failures.
		}
	}, [storageKey]);

	return { readDraft, saveDraft, clearDraft };
}

/** Native "leave site?" prompt while there are unsaved scorecard changes. */
export function useBeforeUnloadGuard(enabled: boolean) {
	useEffect(() => {
		if (!enabled) return;
		const handler = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [enabled]);
}
